/**
 * Currency ↔ amount spacing.
 *
 * `template.moneyFormat` is the structured source of truth. The server owns the
 * render: the `{{money}}` helper emits the markup and `--money-gap` is injected
 * into the stylesheet the PDF is printed from. Everything here mirrors
 * documentTemplates/constants/moneyFormat.js + utils/moneyFormat.js so the
 * canvas and the live preview look like the PDF before the template is saved.
 *
 * Only `currencyAmountGap` is ever sent. The server also accepts a free-form
 * `gap` length, but its validator dereferences an undefined `CUSTOM_GAP`, so a
 * custom gap would throw on save.
 */

export const DEFAULT_MONEY_GAP = "normal";

/** Fallback for an unavailable /money-format-presets — the server list wins. */
export const MONEY_GAP_PRESETS = [
  {
    key: "none",
    label: "None",
    gap: "0",
    separator: null,
    example: "INR94400.00",
  },
  {
    key: "thin",
    label: "Thin",
    gap: "0.1em",
    separator: "\u2009",
    example: "INR\u200994,400.00",
  },
  {
    key: "normal",
    label: "Normal",
    gap: "0.25em",
    separator: " ",
    example: "INR 94,400.00",
  },
  {
    key: "wide",
    label: "Wide",
    gap: "0.5em",
    separator: "  ",
    example: "INR  94,400.00",
  },
];

const PRESET_BY_KEY = MONEY_GAP_PRESETS.reduce((acc, preset) => {
  acc[preset.key] = preset;
  return acc;
}, {});

export const isMoneyGapPreset = (key) =>
  Object.prototype.hasOwnProperty.call(
    PRESET_BY_KEY,
    String(key ?? "").trim().toLowerCase()
  );

/** Preset key from a stored moneyFormat, falling back to the default. */
export const resolveMoneyGap = (moneyFormat) => {
  const key = String(moneyFormat?.currencyAmountGap ?? "").trim().toLowerCase();
  return isMoneyGapPreset(key) ? key : DEFAULT_MONEY_GAP;
};

export const resolveMoneyFormat = (moneyFormat) => ({
  currencyAmountGap: resolveMoneyGap(moneyFormat),
});

export const moneyGapCss = (moneyFormat) =>
  PRESET_BY_KEY[resolveMoneyGap(moneyFormat)].gap;

/**
 * Literal character placed between the currency and the amount. `null` for the
 * `none` preset, which emits no separator element at all.
 */
export const moneyGapSeparator = (moneyFormat) =>
  PRESET_BY_KEY[resolveMoneyGap(moneyFormat)].separator;

export const moneyFormatsEqual = (a, b) =>
  resolveMoneyGap(a) === resolveMoneyGap(b);

const escapeHtml = (value = "") =>
  String(value ?? "")
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;");

const FORMATTED_AMOUNT = /^(?:-?\d{1,3}(?:,\d{3})*|-?\d+)(?:\.\d+)?$/;

/** True when the value already reads as a display amount. */
const isFormattedAmount = (value) => {
  if (value === null || value === undefined) return false;
  const str = String(value).trim();
  if (!str) return false;
  if (str.includes(",")) return true;
  return FORMATTED_AMOUNT.test(str) && str.includes(".");
};

/** Mirrors the server so a previewed amount matches the printed one. */
export const formatMoneyAmount = (value) => {
  if (value === null || value === undefined || value === "") return "0.00";

  const str = String(value).trim();
  if (isFormattedAmount(str)) return str;

  const num = Number(str.replace(/,/g, ""));
  if (Number.isNaN(num)) return str;

  return num.toLocaleString("en-IN", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });
};

/**
 * The markup `{{money}}` produces on the server. Used for the live preview so
 * it is byte-identical to what the PDF renderer lays out.
 */
export const buildMoneyHtml = (currency, amount, { moneyFormat, wrap } = {}) => {
  const currencyStr = escapeHtml(String(currency ?? "").trim());
  const amountStr = escapeHtml(formatMoneyAmount(amount));
  const separator = moneyGapSeparator(moneyFormat);

  const gap = separator
    ? `<span class="money-gap">${escapeHtml(separator)}</span>`
    : "";

  let inner = `<span class="money"><span class="money-currency">${currencyStr}</span>${gap}<span class="money-amount">${amountStr}</span></span>`;

  if (wrap === "parens") {
    inner = `(${inner})`;
  }

  return inner;
};

/**
 * The `.money` rules the server injects into every rendered document. The
 * gap is a margin on `.money-gap` itself, not a flexbox `gap` on `.money` -
 * flexbox `gap` inserts space on BOTH sides of every flex child, so with 3
 * children (currency, the separator span, amount) it doubled the configured
 * preset into two gaps' worth of visible space. A single margin gives
 * exactly the one gap the preset (and its "wide"/"normal" preview text)
 * actually describes. `.money`'s own `gap` is explicitly zeroed, not just
 * omitted - a template saved before this fix still carries the old
 * `.money{gap:...}` rule in its own stored `css`, appended earlier in the
 * stylesheet, and an omitted property never overrides an earlier rule's
 * declaration of that same property. Mirrors the server's identical fix in
 * documentTemplates/utils/moneyFormat.js.
 */
export const buildMoneyFormatCss = (moneyFormat) =>
  `:root{--money-gap:${moneyGapCss(moneyFormat)};}
.money{display:inline-flex;align-items:baseline;white-space:nowrap;gap:0;}
.money-currency{flex:0 0 auto;}
.money-gap{flex:0 0 auto;margin-inline-start:var(--money-gap,0.25em);}
.money-amount{flex:0 0 auto;}`;

/**
 * Canvas variant. Templates saved before `{{money}}` existed carry their own
 * `.money` rules in `template.css`, so the generated rule is given a doubled
 * class to outrank them, and the gap is restated on the element itself rather
 * than left to a `:root` variable the template may also define. `.money
 * .money-gap` (a descendant selector, not a compound one - `.money-gap`
 * never also carries the `money` class) matches the same specificity trick
 * for the same reason.
 */
export const buildCanvasMoneyCss = (moneyFormat) => {
  const gap = moneyGapCss(moneyFormat);

  return `${buildMoneyFormatCss(moneyFormat)}
.money.money{display:inline-flex;align-items:baseline;white-space:nowrap;gap:0;}
.money.money > *{flex:0 0 auto;}
.money .money-gap{margin-inline-start:${gap};}
.money[data-hb-wrap="parens"]::before{content:"(";flex:0 0 auto;}
.money[data-hb-wrap="parens"]::after{content:")";flex:0 0 auto;}`;
};

/** Normalise whatever /money-format-presets returns into preset entries. */
export const normalizeMoneyPresets = (payload) => {
  const raw =
    (Array.isArray(payload) && payload) ||
    (Array.isArray(payload?.presets) && payload.presets) ||
    (Array.isArray(payload?.data?.presets) && payload.data.presets) ||
    (Array.isArray(payload?.data) && payload.data) ||
    [];

  const presets = raw
    .filter((preset) => preset && isMoneyGapPreset(preset.key))
    .map((preset) => {
      const key = String(preset.key).trim().toLowerCase();
      const fallback = PRESET_BY_KEY[key];
      return {
        key,
        label: preset.label || fallback.label,
        gap: preset.gap || fallback.gap,
        example: preset.example || fallback.example,
        // The server does not publish the separator; it is part of the contract
        separator: fallback.separator,
      };
    });

  return presets.length ? presets : MONEY_GAP_PRESETS;
};
